import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";

import gameImg1 from "../../assets/images/games/samplegameImg.png";
import { getCachedSearchCatalog, loadSearchCatalog } from "../../services/gameSearchCatalog";
import { getCloudFrontSourcePath } from "../../utils/functions";

const HOME_SCROLL_Y_KEY = "akedoHomeScrollY";
const HOME_PENDING_SCROLL_RESTORE_KEY = "akedoHomePendingScrollRestore";

const normalizeSearchValue = (value) => String(value ?? "").trim().toLowerCase();

const getSearchableValues = (game) => [
	game?.name,
	game?.packageName,
	game?.package_name,
	game?.package,
	game?.id,
	game?.game,
];

const HomeSearch = ({ games = [] }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [catalogGames, setCatalogGames] = useState(() => getCachedSearchCatalog() || []);
	const [isCatalogLoading, setIsCatalogLoading] = useState(false);
	const [leftOffset, setLeftOffset] = useState(null);
	const [controlSize, setControlSize] = useState(null);
	const inputRef = useRef(null);
	const navigate = useNavigate();
	const cloudFrontSourcePath = getCloudFrontSourcePath();
	const searchableGames = useMemo(() => {
		const gamesById = new Map();
		catalogGames.forEach((game) => gamesById.set(game.id, game));
		games.forEach((game) => {
			if (!game?.id) {
				return;
			}

			gamesById.set(game.id, {
				...gamesById.get(game.id),
				...game,
			});
		});
		return Array.from(gamesById.values());
	}, [catalogGames, games]);

	const results = useMemo(() => {
		const normalizedQuery = normalizeSearchValue(query);
		if (normalizedQuery.length < 2) {
			return [];
		}

		return searchableGames.filter((game) =>
			getSearchableValues(game).some((value) =>
				normalizeSearchValue(value).includes(normalizedQuery)
			)
		);
	}, [query, searchableGames]);

	const ensureCatalogLoaded = useCallback(async () => {
		const cached = getCachedSearchCatalog();
		if (cached) {
			setCatalogGames(cached);
			return;
		}

		setIsCatalogLoading(true);
		try {
			setCatalogGames(await loadSearchCatalog());
		} catch (error) {
			// Keep the homepage-derived games as a usable offline fallback.
		} finally {
			setIsCatalogLoading(false);
		}
	}, []);

	const closeSearch = () => {
		setIsOpen(false);
		setQuery("");
	};

	const openSearch = () => {
		setQuery("");
		setIsOpen(true);
		void ensureCatalogLoaded();
	};

	const clearQuery = () => {
		setQuery("");
		inputRef.current?.focus();
	};

	const handleResultClick = (game) => {
		if (!game?.id) {
			return;
		}

		sessionStorage.setItem(HOME_SCROLL_Y_KEY, String(window.scrollY || 0));
		sessionStorage.setItem(HOME_PENDING_SCROLL_RESTORE_KEY, "1");
		closeSearch();
		navigate(`/details/${game.id}`);
	};

	useLayoutEffect(() => {
		let frameId = null;

		const updateLeftOffset = () => {
			const profilePanel = document.querySelector("header .profile");
			if (!profilePanel) {
				return;
			}

			const { height, right } = profilePanel.getBoundingClientRect();
			setLeftOffset(Math.max(15, Math.round(window.innerWidth - right)));
			setControlSize(Math.max(36, Math.round(height)));
		};

		const scheduleUpdate = () => {
			if (frameId) {
				return;
			}

			frameId = window.requestAnimationFrame(() => {
				frameId = null;
				updateLeftOffset();
			});
		};

		updateLeftOffset();
		window.addEventListener("resize", scheduleUpdate);

		return () => {
			if (frameId) {
				window.cancelAnimationFrame(frameId);
			}
			window.removeEventListener("resize", scheduleUpdate);
		};
	}, []);

	const controlStyle = leftOffset == null && controlSize == null
		? undefined
		: {
			...(leftOffset == null ? {} : { "--home-search-left": `${leftOffset}px` }),
			...(controlSize == null ? {} : { "--home-search-size": `${controlSize}px` }),
		};

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const focusTimer = window.setTimeout(() => {
			inputRef.current?.focus();
		}, 0);

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				closeSearch();
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.clearTimeout(focusTimer);
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen]);

	if (!isOpen) {
		return (
			<div className="homeSearchControl" style={controlStyle}>
				<button
					type="button"
					className="homeSearchIconButton"
					aria-label="Search games"
					onClick={openSearch}
				>
					<FeatherIcon icon="search" size={20} />
				</button>
			</div>
		);
	}

	return (
		<>
			<button
				type="button"
				className="homeSearchDimLayer"
				aria-label="Close search"
				onClick={closeSearch}
			/>
			<div className="homeSearchControl homeSearchControlOpen" style={controlStyle}>
				<div className="homeSearchPanel">
					<FeatherIcon icon="search" size={18} className="homeSearchPanelIcon" />
					<input
						ref={inputRef}
						value={query}
						type="search"
						placeholder="Search Games"
						className="homeSearchInput"
						onChange={(event) => setQuery(event.target.value)}
					/>
					{isCatalogLoading ? (
						<span
							className="homeSearchLoadingSpinner"
							role="status"
							aria-label="Loading games"
						/>
					) : query && (
						<button
							type="button"
							className="homeSearchClearButton"
							aria-label="Clear search"
							onClick={clearQuery}
						>
							<FeatherIcon icon="x" size={18} />
						</button>
					)}
				</div>
				{query.trim().length >= 2 && results.length > 0 && (
					<div className="homeSearchResults">
						{results.map((game) => {
							const iconSrc = game?.icon
								? `${cloudFrontSourcePath}${game.icon}`
								: gameImg1;

							return (
								<button
									type="button"
									className="homeSearchResultItem"
									key={`home_search_${game.id}`}
									onClick={() => handleResultClick(game)}
								>
									<span className="homeSearchResultIcon">
										<img src={iconSrc} alt="" onError={(event) => { event.currentTarget.src = gameImg1; }} />
									</span>
									<span className="homeSearchResultName">{game?.name}</span>
								</button>
							);
						})}
					</div>
				)}
			</div>
		</>
	);
};

export default React.memo(HomeSearch);
